import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { VideoGridLayout } from "./VideoGridLayout";
import ControlsPanel from "./controls/ControlsPanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faMessage } from "@fortawesome/free-solid-svg-icons";
import { useMediaDevices } from "../../../hooks/useMediaDevices";
import { PresentationLayout } from "./PresentationLayout";
import { SettingsModal } from "./controls/SettingsModal";
import RecordButton from "./controls/RecordButton";
import LeaveConfirmationModal from "../../ui/ConfirmationModals/LeaveConfirmationModal";
import { ValidateOtpResponseDto } from "../../../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import VideoChatWindow from "./VideoChatWindow";
import { VideoCallState } from "../../../interfaces/videocallDtos/videoCall";
import { isNewMessageReceivedSubject } from "../../../services/Subjects/ChatSubjects";
import { NetworkQuality } from "../../../interfaces/video/video";
import { useVideoRoom } from "../../../hooks/useVideoRoom";
import VideoChatHeader from "./controls/VideoChatHeader";

const BackendVideoRoom: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const lobbyState = location.state as VideoCallState;
    const { roomId } = useParams();
    // UI State
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [messagesOpen, setMessagesOpen] = useState(false);
    const [showLeaveDialog, setShowLeaveDialog] = useState(false);
    // 1. Add state to manage tooltip visibility
    const [showRecordTooltip, setShowRecordTooltip] = useState(false);
    const [isNewMessageReceived, setIsNewMessageReceived] = useState<boolean>(false);
    const [isCalling, setIsCalling] = useState<boolean>(true);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

    // State to manage screen sharing page responsive design
    const [showParticipantGrid , setShowParticipantsGrid] = useState<boolean>(true);

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    function getExternalUserDetails(): ValidateOtpResponseDto | null {
        const raw = localStorage.getItem("ExternalUserDetails");
        return raw ? JSON.parse(raw) as ValidateOtpResponseDto : null;
    }

    useEffect(() => {
        if(isMobileView) { 
            setShowParticipantsGrid(false);
        }
    } , [isMobileView]);

    // Custom Hooks
    const isPublisherFromLocal = localStorage.getItem("isPublisher");
    const { videoDevices, audioDevices } = useMediaDevices();
    const {
        localParticipant,
        remoteFeeds,
        isSharingScreen,
        activeScreenShare,
        actions,
        selectedVideoDevice,
        selectedAudioDevice,
    } = useVideoRoom(
        isPublisherFromLocal == "true", Number(lobbyState.relatedChatConversationId), lobbyState.calenderEventId ?? null,
        lobbyState.isOneToOneCall,
        lobbyState?.displayName || 'Guest',
        lobbyState?.videoDeviceId,
        lobbyState?.audioDeviceId,
        getExternalUserDetails(),
        lobbyState?.isMicMuted ?? false,
        lobbyState?.isCameraOn ?? false
    );

    const handleToggleSettings = () => setIsSettingsOpen(!isSettingsOpen);

    // 2. Create a handler to show the tooltip
    const handleRecordClick = () => {
        // Show tooltip and automatically hide it after 3 seconds
        if (!showRecordTooltip) {
            setShowRecordTooltip(true);
            setTimeout(() => {
                setShowRecordTooltip(false);
            }, 3000);
        }
    };

    /**
     * ========================
     * Participants Logic 
     * ======================== 
     **/

    let allParticipants = [localParticipant, ...Object.values(remoteFeeds)];

    // FOR TESTING PAGINATION: Uncomment the block below to simulate 15 participants.
    // ===============================================================================
    /*
    const mockParticipants = Array.from({ length: 15 }, (_, i) => ({
      feedId: `mock-participant-${i + 1}`,
      displayName: `Mock User ${i + 1}`,
      isLocal: false,
      stream: null,
      isMuted: i % 3 === 0,
      isCameraOn: i % 2 === 0,
      userId: `mock-user-id-${i + 1}`,
      networkQuality: 'good' as NetworkQuality,
      latency: 10,
    }));
    allParticipants = [localParticipant, ...mockParticipants];
    */
    // ===============================================================================

    const screenSharingParticipant = allParticipants.find(p => p.feedId === activeScreenShare?.feedId);
    const screenSharerDisplayName = screenSharingParticipant?.displayName;

    useEffect(() => {
        // Handle before unload
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        // Subscribe to new message updates
        isNewMessageReceivedSubject.next(false);
        const subscription = isNewMessageReceivedSubject.subscribe(isNewMsg => {
            setIsNewMessageReceived(isNewMsg);
        });

        // Cleanup
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        // effect for updating the isCalling indicator
        if (isCalling && Object.keys(remoteFeeds).length !== 0 && lobbyState.isPublisher) {
            setIsCalling(false);
        }

    }, [remoteFeeds])


    const handleOnLeftCancel = () => setShowLeaveDialog(false);
    const handleOnLeftConfirm = () => {
        const externalUserDetails = getExternalUserDetails();
        actions.handleHangUp();
        setShowLeaveDialog(false);
        if (externalUserDetails) {
            window.close();
        } else {
            navigate(lobbyState.relatedChatConversationId ? `/chat/${lobbyState.relatedChatConversationId}` : "/home")
        }
    };
    const handleHangUpClick = () => setShowLeaveDialog(true);

    return (
        <>
      {/* Full Screen height and width */}
      <div className="flex h-screen w-full bg-[#F9F6FF] text-light overflow-hidden">
        <div className="h-full w-full relative">
          {/* Header component */}
          <VideoChatHeader
            roomId={roomId}
            handleRecordClick={handleRecordClick}
            messageOpen={messagesOpen}
            setMessageOpen={setMessagesOpen}
            isNewMessageReceived={isNewMessageReceived}
            isNewMessageReceivedSubject={isNewMessageReceivedSubject}
            isMobileView={isMobileView}
            showParticipantGrid={showParticipantGrid}
            setShowParticipantsGrid={setShowParticipantsGrid}
            isSharingScreen={isSharingScreen}
          />

          {/* Main Video/Presentation Content */}
          <div className="w-full h-full" 
            style={{
                paddingBottom: true ? "120px" : ""
            }}
          >
            {isSharingScreen && activeScreenShare?.stream ? (
              <PresentationLayout
                participants={allParticipants}
                screenShareFeed={activeScreenShare}
                screenSharerDisplayName={screenSharerDisplayName ?? ""}
                showParticipantGrid={showParticipantGrid}
              />
            ) : (
              <VideoGridLayout participants={allParticipants} />
            )}
          </div>

          {/* Controls */}
          {(!isMobileView || !messagesOpen) && (
            <div
              className="absolute bottom-0 w-full flex justify-center bg-[#E1DBEB]"
              style={{ zIndex: 30 }}
            >
              <ControlsPanel
                muted={localParticipant.isMuted}
                onToggleMute={actions.handleToggleMute}
                cameraOn={localParticipant.isCameraOn}
                onToggleCamera={actions.handleToggleCamera}
                hangUp={handleHangUpClick}
                shared={isSharingScreen}
                onShareScreen={actions.handleShareScreen}
                onToggleMesages={() => setMessagesOpen((p) => !p)}
                messagesOpen={messagesOpen}
                onToggleSettings={handleToggleSettings}
                settingsOpen={isSettingsOpen}
              />
            </div>
          )}
        </div>

        {/* Chat Window */}
        <div
            className="video_confrence_chat flex-shrink-0 bg-[#1c1c1c] transition-all duration-300 ease-in-out overflow-hidden"
            style={{
                width: messagesOpen ? (isMobileView ? '100%' : '400px') : '0px',
                ...(isMobileView && messagesOpen && {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    zIndex: 50,
                })
            }}
        >
            {/* Inner container has a fixed width to prevent content from squishing during animation */}
             <div id="chat-window" className="chat-video h-100" style={{ width: isMobileView ? '100%' : '400px' }}>
                 {lobbyState.relatedChatConversationId && (
                    <VideoChatWindow
                        messagesOpen={messagesOpen}
                        conversationId={Number(lobbyState.relatedChatConversationId)}
                        onClose={() => setMessagesOpen(false)}
                    />
                )}
                {lobbyState.calenderEventId && (
                    <>
                        <VideoChatWindow
                            messagesOpen={messagesOpen}
                            calendarEventId={lobbyState.calenderEventId}
                            externalUserDetails={getExternalUserDetails()}
                            onClose={() => setMessagesOpen(false)}
                        />
                    </>
                )}
            </div>
        </div>

        {/* Modals are kept outside the main layout flow */}
         {isSettingsOpen && (
            <SettingsModal
                videoDevices={videoDevices}
                audioDevices={audioDevices}
                selectedVideoDevice={selectedVideoDevice}
                selectedAudioDevice={selectedAudioDevice}
                onChangeDevice={actions.handleChangeDevice}
                onClose={() => setIsSettingsOpen(false)}
            />
        )}

        {showLeaveDialog && (
            <LeaveConfirmationModal
                isLoading={false}
                onCancel={handleOnLeftCancel}
                onConfirm={handleOnLeftConfirm}
                confirmButtonText="Hang Up"
            >
                Do you want to hang up the call?
            </LeaveConfirmationModal>
        )} 
      </div>
    </>
    );
};

export default BackendVideoRoom;